import {
  toWSDateString,
  WSButton,
  WSDivider,
  WSFlexBox,
  WSTable,
  WSTableCell,
  WSText
} from "@wingspanhq/fe-component-library";
import { IPayeeTaxFormResponse } from "@wingspanhq/payments/dist/interfaces";
import { InvoiceRow, InvoiceStatus } from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { queriesInvoiceRows } from "@query";
import {
  Route,
  useHistory,
  useLocation,
  useRouteMatch
} from "react-router-dom";
import { RouteInvoiceDetails } from "../../../../Invoices/screens/RouteInvoiceDetails";
import { WSQueries } from "../../../../query/WSQuery";
import { ButtonDownloadCsv } from "../../../../shared/components/ButtonDownloadCsv";
import { selectorPayeeTaxFormPayerName } from "../../../TaxCorrections/selectors/selectorPayeeTaxFormPayerName";

type Props = {
  taxForm: IPayeeTaxFormResponse;
};

export const Payments: React.FC<Props> = ({ taxForm }) => {
  const queryInvoices = queriesInvoiceRows.searchInvoiceRows.useQueryAll({
    filter: {
      payerId: { eq: taxForm.clientId },
      status: { eq: InvoiceStatus.PAID },
      events_paidAt: {
        gte: new Date(Date.UTC(taxForm.year, 0, 1)),
        lt: new Date(Date.UTC(taxForm.year + 1, 0, 1))
      }
    }
  });
  const payerName = selectorPayeeTaxFormPayerName(taxForm);
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();

  return (
    <>
      <Route
        path={match.path + "/payments/:invoiceId"}
        component={RouteInvoiceDetails}
      />

      <WSFlexBox.CenterY wrap="nowrap" justify="space-between">
        <WSText weight="medium">Invoices</WSText>

        {queryInvoices.flatData && queryInvoices.flatData.length > 0 && (
          <ButtonDownloadCsv
            getData={async () => {
              const invoices = queryInvoices.flatData || [];
              return invoices.map(invoice => ({
                "Invoice Number": invoice.invoiceNumber,
                "Non-employee compensation": invoice.amount,
                "Paid Date": invoice.events?.paidAt
              }));
            }}
            fileName={`${payerName} Tax Form Invoices`}
            customTrigger={<WSButton.Link>Download CSV</WSButton.Link>}
          />
        )}
      </WSFlexBox.CenterY>
      <WSDivider mt="M" mb="2XL" />
      <WSQueries queries={{ queryInvoices }}>
        {({ queryInvoices: { flatData: invoices } }) => {
          return (
            <>
              <WSTable<InvoiceRow>
                tableData={(invoices ?? []).map(invoice => ({
                  id: invoice.id as string,
                  data: invoice
                }))}
                onRowClick={({ data: invoice }) => {
                  history.push(location.pathname + "/payments/" + invoice.id, {
                    backPath: location.pathname
                  });
                }}
                columns={[
                  {
                    config: {
                      gridTemplateSizeMax: "1fr",
                      header: {
                        text: `Invoices paid in ${taxForm.year}`
                      }
                    },
                    renderFunction: ({ data: payable }) => (
                      <WSTableCell
                        avatar={{
                          type: "icon",
                          icon: "check-double",
                          color: "green500",
                          colorBackground: "green50"
                        }}
                        text={payable.invoiceNumber}
                      />
                    )
                  },
                  {
                    config: {
                      gridTemplateSizeMax: "1fr",
                      justify: "start",
                      header: {
                        text: "Non-employee compensation"
                      }
                    },
                    renderFunction: ({ data: payable }) => (
                      <WSTableCell
                        text={
                          payable.amount === undefined || !payable.currency
                            ? "-"
                            : formatMoney(payable.amount, payable.currency)
                        }
                        secondaryText={
                          payable.events?.paidAt
                            ? `Paid on ${toWSDateString(
                                payable.events.paidAt,
                                "monthDate"
                              )}`
                            : undefined
                        }
                      />
                    )
                  }
                ]}
              />
              {(invoices ?? []).length === 0 && (
                <WSText.ParagraphSm align="center" color="gray400" mt="M">
                  No invoices found
                </WSText.ParagraphSm>
              )}
            </>
          );
        }}
      </WSQueries>
    </>
  );
};
